import { DEFAULT_TERM_ID } from '../../constants';
import { OmakasePlanPolicies, OmakasePlanTerms } from '../../constants/omakasePlan';
import {
  CHANGE_INPUT_BUDGET,
  CHANGE_INPUT_TERM,
  CHANGE_INPUT_POLICY,
  SET_CREATING_STRATEGY_LIST,
  SET_CREATING_INPUT,
  SET_CREATING_RESULT,
  SET_CREATING_INPUT_SET_COUNT,
  SET_CREATING_INPUT_SET_COUNT_ALL,
  RESET_CREATING_INPUT_SET_COUNT,
  EDIT_CREATING_ERROR_MESSAGES,
  SET_CREATING_STRATEGY_LIST_CACHE,
  CHANGE_CREATING_SIMULATION_TERM_ID,
  CHANGE_CREATING_SELECTED_STRATEGY_ID,
  SET_OMAKASE_PLAN_MASTER,
  SET_OMAKASE_PLAN_LOADING,
  INITIALIZE_OMAKASE_INPUT,
  SET_OMAKASE_PLAN_SUBMIT_PROCESSING,
  GET_PORTFOLIOS_REQUEST_START_LOADING,
  GET_PORTFOLIOS_REQUEST_END_LOADING,
  GET_PORTFOLIOS_SUCCESS,
  GET_PORTFOLIO_DETAIL_REQUEST_START_LOADING,
  GET_PORTFOLIO_DETAIL_REQUEST_END_LOADING,
  GET_PORTFOLIO_DETAIL_SUCCESS,
  CREATE_AP_GROUP_REQUEST_START_LOADING,
  CREATE_AP_GROUP_REQUEST_END_LOADING,
  CHANGE_NAME_REQUEST_START_LOADING,
  CHANGE_NAME_REQUEST_END_LOADING,
  GET_OMAKASE_PLAN_MASTER_REQUEST_START_LOADING,
  GET_OMAKASE_PLAN_MASTER_REQUEST_END_LOADING,
  GET_OMAKASE_PLAN_MASTER_SUCCESS,
} from '../actionConstants/omakasePlanConstants';

const OMAKASE_PLAN_RESULT_STATIC_PROPS = ['max_dd', 'margin_required', 'margin_recommend', 'return'];

const initialState = Object.freeze({
  inputForm: {
    budget: 5,
    term: OmakasePlanTerms.LONG,
    policy: OmakasePlanPolicies.RETURN,
    // errorMessages: [], // TODO
  },
  /* omakasePlanMaster samples
    {name: data}
    name =>
        "short-return"
        "short-balance"
        "mid-return"
        "mid-balance"
        "long-return"
        "long-balance"
    data => [
        {
            "10001":0,
            "10002":0,
            "10003":0,
            "10004":0,
            "10005":1,
            "10006":1,
            "10007":0,
            "max_dd":42929.9021,
            "margin_required":197606,
            "margin_recommend":240535.9021,
            "return":1.819
        },
        {
            "10001":1,
            "10002":0,
            "10003":0,
            "10004":0,
            "10005":1,
            "10006":1,
            "10007":0,
            "max_dd":56483.3445,
            "margin_required":270765,
            "margin_recommend":327248.3445,
            "return":1.974
        }
    ]
   */
  omakasePlanMaster: {},
  creating: {
    input: {},
    result: {}, // { omakasePlanId, selectedOmakasePlan, defaultSimulationTermId }
    strategyList: [],
    inputSetCount: {}, // "strategyId": count
    errorMessages: [],
    strategyListCache: {}, // [simulationTermId]: simulationStats
    selectedSimulationTermId: String(DEFAULT_TERM_ID),
    selectedStrategyId: null,
  },
  submit: {
    isProcessing: false,
  },
  arePortfoliosLoading: false,
  portfolios: [],
  isPortfolioDetailLoading: false,
  portfolioDetail: {},
  isCreateApGroupLoading: false,
  isChangeNameLoading: false,
  isOmakasePlanMasterLoading: false,
  loading: false,
});

const createDefaultSetCount = (state) => {
  const {
    creating: { strategyList = [] },
  } = state || {};
  return strategyList.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.strategyId]: 0,
    }),
    {},
  );
};
const getOmakasePlanResultStrategies = (selectedOmakasePlan = {}, defaults = {}) => {
  return Object.fromEntries(
    Object.entries(selectedOmakasePlan)
      .filter(([key]) => !OMAKASE_PLAN_RESULT_STATIC_PROPS.find((k) => k === key))
      .filter(([key]) => Object.keys(defaults).find((k) => String(k) === String(key))),
  );
};
const createOmakasePlanDefaultSetCount = (state) => {
  const selectedOmakasePlan = state?.creating?.result?.selectedOmakasePlan || {};
  const defaults = createDefaultSetCount(state);
  return {
    ...defaults,
    ...getOmakasePlanResultStrategies(selectedOmakasePlan, defaults),
  };
};

export default (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case CHANGE_INPUT_BUDGET: {
      const {
        inputForm: { budget },
      } = payload;
      return {
        ...state,
        inputForm: {
          ...state.inputForm,
          budget,
        },
      };
    }
    case CHANGE_INPUT_TERM: {
      const {
        inputForm: { term },
      } = payload;
      return {
        ...state,
        inputForm: {
          ...state.inputForm,
          term,
        },
      };
    }
    case CHANGE_INPUT_POLICY: {
      const {
        inputForm: { policy },
      } = payload;
      return {
        ...state,
        inputForm: {
          ...state.inputForm,
          policy,
        },
      };
    }
    case SET_CREATING_STRATEGY_LIST: {
      const {
        creating: { strategyList },
      } = payload;
      const selectedStrategyId = strategyList?.[0]?.strategyId || null;
      return {
        ...state,
        creating: { ...state.creating, strategyList, selectedStrategyId },
      };
    }
    case SET_CREATING_INPUT: {
      const {
        creating: { input },
      } = payload;
      return {
        ...state,
        creating: { ...initialState.creating, input },
      };
    }
    case SET_CREATING_RESULT: {
      const {
        creating: { result = {} },
      } = payload;
      const { defaultSimulationTermId = state.creating.selectedSimulationTermId } = result;
      return {
        ...state,
        creating: { ...state.creating, result, selectedSimulationTermId: defaultSimulationTermId },
      };
    }
    case SET_CREATING_INPUT_SET_COUNT: {
      const {
        creating: {
          inputSetCount: { strategyId, count },
        },
      } = payload;
      return {
        ...state,
        creating: {
          ...state.creating,
          inputSetCount: {
            ...state.creating.inputSetCount,
            [strategyId]: count,
          },
        },
      };
    }
    case SET_CREATING_INPUT_SET_COUNT_ALL: {
      const {
        creating: { inputSetCount },
      } = payload;
      return {
        ...state,
        creating: {
          ...state.creating,
          inputSetCount,
        },
      };
    }
    case RESET_CREATING_INPUT_SET_COUNT: {
      return {
        ...state,
        creating: {
          ...state.creating,
          inputSetCount: createOmakasePlanDefaultSetCount(state),
          errorMessages: [],
        },
      };
    }
    case EDIT_CREATING_ERROR_MESSAGES: {
      const { editor } = payload;
      const current = state.creating.errorMessages;
      const errorMessages = editor?.(current) ?? current;
      return {
        ...state,
        creating: {
          ...state.creating,
          errorMessages,
        },
      };
    }
    case SET_CREATING_STRATEGY_LIST_CACHE: {
      const { simulationTermId, strategyList } = payload;
      return {
        ...state,
        creating: {
          ...state.creating,
          strategyListCache: {
            ...state.creating.strategyListCache,
            [simulationTermId]: strategyList,
          },
        },
      };
    }
    case CHANGE_CREATING_SIMULATION_TERM_ID: {
      const { simulationTermId } = payload;
      return {
        ...state,
        creating: {
          ...state.creating,
          selectedSimulationTermId: simulationTermId,
        },
      };
    }
    case CHANGE_CREATING_SELECTED_STRATEGY_ID: {
      const { strategyId } = payload;
      return {
        ...state,
        creating: {
          ...state.creating,
          selectedStrategyId: strategyId,
        },
      };
    }
    case SET_OMAKASE_PLAN_MASTER: {
      const { omakasePlanMaster } = payload;
      return {
        ...state,
        omakasePlanMaster,
      };
    }
    case SET_OMAKASE_PLAN_LOADING: {
      const { loading } = payload;
      return {
        ...state,
        loading,
      };
    }
    case INITIALIZE_OMAKASE_INPUT: {
      return {
        ...state,
        inputForm: initialState.inputForm,
        creating: initialState.creating,
      };
    }
    case SET_OMAKASE_PLAN_SUBMIT_PROCESSING: {
      const { isProcessing } = payload;
      return {
        ...state,
        submit: {
          ...state.confirm,
          isProcessing,
        },
      };
    }
    case GET_PORTFOLIOS_REQUEST_START_LOADING: {
      return {
        ...state,
        arePortfoliosLoading: true,
      };
    }
    case GET_PORTFOLIOS_REQUEST_END_LOADING: {
      return {
        ...state,
        arePortfoliosLoading: false,
      };
    }
    case GET_PORTFOLIOS_SUCCESS: {
      const { portfolios } = payload;
      return {
        ...state,
        portfolios,
      };
    }
    case GET_PORTFOLIO_DETAIL_REQUEST_START_LOADING: {
      return {
        ...state,
        isPortfolioDetailLoading: true,
      };
    }
    case GET_PORTFOLIO_DETAIL_REQUEST_END_LOADING: {
      return {
        ...state,
        isPortfolioDetailLoading: false,
      };
    }
    case GET_PORTFOLIO_DETAIL_SUCCESS: {
      const { portfolioDetail } = payload;
      return {
        ...state,
        portfolioDetail,
      };
    }
    case CREATE_AP_GROUP_REQUEST_START_LOADING: {
      return {
        ...state,
        isCreateApGroupLoading: true,
      };
    }
    case CREATE_AP_GROUP_REQUEST_END_LOADING: {
      return {
        ...state,
        isCreateApGroupLoading: false,
      };
    }
    case CHANGE_NAME_REQUEST_START_LOADING: {
      return {
        ...state,
        isChangeNameLoading: true,
      };
    }
    case CHANGE_NAME_REQUEST_END_LOADING: {
      return {
        ...state,
        isChangeNameLoading: false,
      };
    }
    case GET_OMAKASE_PLAN_MASTER_REQUEST_START_LOADING: {
      return {
        ...state,
        isOmakasePlanMasterLoading: true,
      };
    }
    case GET_OMAKASE_PLAN_MASTER_REQUEST_END_LOADING: {
      return {
        ...state,
        isOmakasePlanMasterLoading: false,
      };
    }
    case GET_OMAKASE_PLAN_MASTER_SUCCESS: {
      const { omakasePlanMaster } = payload;
      return {
        ...state,
        omakasePlanMaster,
      };
    }

    default: {
      return state;
    }
  }
};
